import { useEffect, useRef, useState } from "react";
import { content as baseContent, isContentCompatible, mergeSiteContent } from "@/content";

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_LABELS = {
  nav: "Navigation (Menü-Einträge)",
  hero: "Hero (Einstieg)",
  einsatz: "Wo ich gut einsetzbar bin",
  cases: "Projekte / Case Studies",
  moreProjects: "Weitere Projekte",
  howIWork: "Wie ich arbeite",
  experience: "Berufserfahrung",
  tools: "Tools & Know-how",
  contact: "Kontakt",
  footer: "Footer",
  cv: "CV-Formular-Texte",
};
const SECTION_ORDER = ["nav", "hero", "einsatz", "cases", "moreProjects", "howIWork", "experience", "tools", "contact", "footer", "cv"];
// technische/strukturelle Felder, die niemals editierbar sein sollen
const SKIP_FIELDS = new Set(["id", "key", "testid", "href", "primary", "src", "ratio", "pos", "url", "meta", "plates", "links", "portraitSrc"]);

function setPath(obj, path, value) {
  let cur = obj;
  for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]];
  cur[path[path.length - 1]] = value;
}

const inputCls =
  "w-full border-b border-ink/30 bg-transparent py-2 text-sm font-light focus:border-proof focus:outline-none transition-colors duration-300";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.25em] text-smoke";
const sectionCls = "border border-hairline p-6 md:p-8";

const FieldPair = ({ label, de, en, onDe, onEn, isList, testid }) => {
  const long = isList || (typeof de === "string" && de.length > 90);
  return (
    <div className="grid md:grid-cols-2 gap-5 py-4 border-t border-hairline/50" data-testid={testid}>
      {[["de", "Deutsch", de, onDe], ["en", "English", en, onEn]].map(([lng, lbl, val, fn]) => (
        <div key={lng}>
          <span className={labelCls}>{label} · {lbl}</span>
          {long ? (
            <textarea rows={isList ? 6 : 4} value={val} onChange={(e) => fn(e.target.value)} className={`${inputCls} resize-y leading-relaxed`} />
          ) : (
            <input type="text" value={val} onChange={(e) => fn(e.target.value)} className={inputCls} />
          )}
        </div>
      ))}
    </div>
  );
};

// Generischer Editor: läuft rekursiv durch das Inhaltsdokument.
// Strings → Textfeld, String-Listen → eine Zeile pro Eintrag, Objekte → rekursiv.
const renderNode = (path, deNode, enNode, update, label) => {
  if (typeof deNode === "string") {
    return (
      <FieldPair
        key={path.join(".")}
        testid={`f-${path.join("-")}`}
        label={label}
        de={deNode}
        en={typeof enNode === "string" ? enNode : ""}
        onDe={(v) => update((d) => setPath(d, ["de", ...path], v))}
        onEn={(v) => update((d) => setPath(d, ["en", ...path], v))}
      />
    );
  }
  if (Array.isArray(deNode)) {
    if (deNode.every((x) => typeof x === "string")) {
      return (
        <FieldPair
          key={path.join(".")}
          testid={`f-${path.join("-")}`}
          label={`${label} · eine Zeile pro Eintrag`}
          isList
          de={deNode.join("\n")}
          en={Array.isArray(enNode) ? enNode.join("\n") : ""}
          onDe={(v) => update((d) => setPath(d, ["de", ...path], v.split("\n").filter((s) => s.trim() !== "")))}
          onEn={(v) => update((d) => setPath(d, ["en", ...path], v.split("\n").filter((s) => s.trim() !== "")))}
        />
      );
    }
    return (
      <div key={path.join(".")}>
        {deNode.map((item, i) => (
          <div key={i} className="border-l-2 border-ink/15 pl-4 mt-5">
            <p className={`${labelCls} mb-1`}>{label} · {i + 1}</p>
            {renderNode([...path, i], item, Array.isArray(enNode) ? enNode[i] : undefined, update, `${label} ${i + 1}`)}
          </div>
        ))}
      </div>
    );
  }
  if (deNode && typeof deNode === "object") {
    return (
      <div key={path.join(".")}>
        {Object.keys(deNode)
          .filter((k) => !SKIP_FIELDS.has(k))
          .map((k) => renderNode([...path, k], deNode[k], enNode && typeof enNode === "object" && enNode !== null ? enNode[k] : undefined, update, k))}
      </div>
    );
  }
  return null;
};

export const AdminContent = ({ token }) => {
  const [doc, setDoc] = useState(null);
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRefs = useRef({});

  useEffect(() => {
    fetch(`${API}/api/content`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((remote) => {
        if (isContentCompatible(remote)) {
          setDoc(mergeSiteContent(remote));
        } else {
          // Altes/kein DB-Schema: Editor startet mit den gebündelten Inhalten —
          // ein Speichern schreibt das neue Schema in die Datenbank (Migration).
          setDoc(JSON.parse(JSON.stringify(baseContent)));
          if (remote && remote.de) setStatus("Hinweis: Datenbank enthält ein altes Schema. Editor zeigt die aktuelle Version — einmal speichern übernimmt sie.");
        }
      })
      .catch(() => setDoc(JSON.parse(JSON.stringify(baseContent))));
  }, []);

  if (!doc) return <p className="mt-10 font-mono text-[11px] text-smoke">Lade Inhalte …</p>;

  const update = (fn) => {
    setDoc((d) => {
      const copy = JSON.parse(JSON.stringify(d));
      fn(copy);
      return copy;
    });
    setStatus("");
  };

  const plateRows = (caseId) => {
    const deItem = doc.de.cases.items.find((i) => i.id === caseId);
    const enItem = doc.en.cases.items.find((i) => i.id === caseId);
    return deItem.plates.map((p, idx) => {
      const dePlate = typeof p === "string" ? { label: p } : p;
      const enPlateRaw = enItem?.plates[idx];
      const enPlate = typeof enPlateRaw === "string" ? { label: enPlateRaw } : enPlateRaw || {};
      return { src: dePlate.src || "", ratio: dePlate.ratio, pos: dePlate.pos, labelDe: dePlate.label || "", labelEn: enPlate.label || "" };
    });
  };

  const writePlates = (caseId, rows) =>
    update((d) => {
      const deItem = d.de.cases.items.find((i) => i.id === caseId);
      const enItem = d.en.cases.items.find((i) => i.id === caseId);
      deItem.plates = rows.map((r) =>
        r.src ? { label: r.labelDe, src: r.src, ...(r.ratio ? { ratio: r.ratio } : {}), ...(r.pos ? { pos: r.pos } : {}) } : r.labelDe
      );
      enItem.plates = rows.map((r) =>
        r.src ? { label: r.labelEn, src: r.src, ...(r.ratio ? { ratio: r.ratio } : {}), ...(r.pos ? { pos: r.pos } : {}) } : r.labelEn
      );
    });

  const upload = async (caseId, file, replaceIdx = null) => {
    if (!file) return;
    setStatus("Bild wird hochgeladen …");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/api/admin/upload`, { method: "POST", headers: { "X-Admin-Token": token }, body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Upload fehlgeschlagen");
      }
      const { url } = await res.json();
      const rows = plateRows(caseId);
      if (replaceIdx === null) rows.push({ src: url, labelDe: "", labelEn: "" });
      else rows[replaceIdx] = { ...rows[replaceIdx], src: url };
      writePlates(caseId, rows);
      setStatus("Bild hochgeladen — nicht vergessen: unten speichern.");
    } catch (e) {
      setStatus(`Fehler: ${e.message}`);
    }
  };

  const save = async () => {
    setSaving(true);
    setStatus("");
    try {
      const res = await fetch(`${API}/api/content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-Admin-Token": token },
        body: JSON.stringify(doc),
      });
      if (!res.ok) throw new Error();
      setStatus("Gespeichert. Die Website zeigt die neuen Inhalte beim nächsten Laden.");
    } catch {
      setStatus("Speichern fehlgeschlagen — bitte erneut versuchen.");
    } finally {
      setSaving(false);
    }
  };

  const de = doc.de;
  const en = doc.en;

  const moveRow = (caseId, rows, from, dir) => {
    const to = from + dir;
    if (to < 0 || to >= rows.length) return;
    const next = [...rows];
    [next[from], next[to]] = [next[to], next[from]];
    writePlates(caseId, next);
  };

  const caseImageManager = (caseId) => {
    const rows = plateRows(caseId);
    return (
      <div className="mt-6">
        <p className={`${labelCls} mb-4`}>Bilder ({rows.length})</p>
        <div className="space-y-4">
          {rows.map((r, idx) => (
            <div key={idx} className="border border-hairline p-4 grid md:grid-cols-12 gap-4 items-center" data-testid={`content-plate-${caseId}-${idx}`}>
              <div className="md:col-span-2">
                {r.src ? (
                  <img src={r.src} alt="" className="w-full aspect-[4/3] object-cover border border-ink/20" />
                ) : (
                  <div className="w-full aspect-[4/3] border border-dashed border-ink/30 flex items-center justify-center font-mono text-[9px] uppercase tracking-[0.2em] text-smoke">Platzhalter</div>
                )}
              </div>
              <div className="md:col-span-4">
                <span className={labelCls}>Bildunterschrift DE</span>
                <input data-testid={`content-plate-de-${caseId}-${idx}`} type="text" value={r.labelDe} onChange={(e) => { const next = [...rows]; next[idx] = { ...r, labelDe: e.target.value }; writePlates(caseId, next); }} className={inputCls} />
              </div>
              <div className="md:col-span-4">
                <span className={labelCls}>Bildunterschrift EN</span>
                <input data-testid={`content-plate-en-${caseId}-${idx}`} type="text" value={r.labelEn} onChange={(e) => { const next = [...rows]; next[idx] = { ...r, labelEn: e.target.value }; writePlates(caseId, next); }} className={inputCls} />
              </div>
              <div className="md:col-span-2 flex gap-2 md:justify-end items-center">
                <div className="flex flex-col gap-1">
                  <button
                    data-testid={`content-plate-up-${caseId}-${idx}`}
                    onClick={() => moveRow(caseId, rows, idx, -1)}
                    disabled={idx === 0}
                    aria-label="Nach oben"
                    className="border border-ink/30 w-7 h-6 font-mono text-[10px] text-smoke hover:border-ink hover:text-ink transition-colors duration-300 disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    data-testid={`content-plate-down-${caseId}-${idx}`}
                    onClick={() => moveRow(caseId, rows, idx, 1)}
                    disabled={idx === rows.length - 1}
                    aria-label="Nach unten"
                    className="border border-ink/30 w-7 h-6 font-mono text-[10px] text-smoke hover:border-ink hover:text-ink transition-colors duration-300 disabled:opacity-30"
                  >
                    ↓
                  </button>
                </div>
                <button data-testid={`content-plate-replace-${caseId}-${idx}`} onClick={() => fileRefs.current[`${caseId}-r-${idx}`]?.click()} className="border border-ink/30 px-3 py-2 font-mono text-[9px] uppercase tracking-[0.15em] text-smoke hover:border-ink hover:text-ink transition-colors duration-300">
                  Ersetzen
                </button>
                <button data-testid={`content-plate-delete-${caseId}-${idx}`} onClick={() => writePlates(caseId, rows.filter((_, i) => i !== idx))} className="border border-ink/30 px-3 py-2 font-mono text-[9px] uppercase tracking-[0.15em] text-smoke hover:border-proof hover:text-proof transition-colors duration-300">
                  Entfernen
                </button>
                <input ref={(el) => (fileRefs.current[`${caseId}-r-${idx}`] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(caseId, e.target.files[0], idx); e.target.value = ""; }} />
              </div>
            </div>
          ))}
        </div>
        <button data-testid={`content-upload-${caseId}`} onClick={() => fileRefs.current[`${caseId}-add`]?.click()} className="mt-4 border border-ink px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-ink hover:text-paper">
          + Bild hinzufügen
        </button>
        <input ref={(el) => (fileRefs.current[`${caseId}-add`] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(caseId, e.target.files[0], null); e.target.value = ""; }} />
      </div>
    );
  };

  return (
    <div className="mt-10 space-y-6" data-testid="content-editor">
      {/* LINKS */}
      <section className={sectionCls}>
        <h3 className="font-serif text-2xl tracking-tight mb-6">Links & Kontaktdaten</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          <div>
            <span className={labelCls}>E-Mail-Adresse</span>
            <input data-testid="content-links-email" type="text" value={de.links.email} onChange={(e) => update((d) => { d.de.links.email = e.target.value; d.en.links.email = e.target.value; })} className={inputCls} />
          </div>
          <div>
            <span className={labelCls}>jolenz.art URL</span>
            <input data-testid="content-links-art" type="text" value={de.links.artUrl} onChange={(e) => update((d) => { d.de.links.artUrl = e.target.value; d.en.links.artUrl = e.target.value; })} className={inputCls} />
          </div>
          <div>
            <span className={labelCls}>Instagram URL (leer = versteckt)</span>
            <input data-testid="content-links-instagram" type="text" value={de.links.instagramUrl || ""} onChange={(e) => update((d) => { d.de.links.instagramUrl = e.target.value; d.en.links.instagramUrl = e.target.value; })} className={inputCls} placeholder="https://instagram.com/…" />
          </div>
          <div>
            <span className={labelCls}>LinkedIn URL (leer = versteckt)</span>
            <input data-testid="content-links-linkedin" type="text" value={de.links.linkedinUrl} onChange={(e) => update((d) => { d.de.links.linkedinUrl = e.target.value; d.en.links.linkedinUrl = e.target.value; })} className={inputCls} placeholder="https://www.linkedin.com/in/…" />
          </div>
        </div>
      </section>

      {/* GENERISCHE SEKTIONEN */}
      {SECTION_ORDER.map((key) => {
        if (key === "cases") {
          return (
            <details key={key} className={sectionCls} data-testid="content-section-cases">
              <summary className="font-serif text-2xl tracking-tight cursor-pointer list-none flex items-center justify-between">
                {SECTION_LABELS[key]}
                <span className="font-mono text-xs text-smoke" aria-hidden="true">+</span>
              </summary>
              <div className="mt-4">
                {renderNode(["cases"], Object.fromEntries(Object.entries(de.cases).filter(([k]) => k !== "items")), Object.fromEntries(Object.entries(en.cases).filter(([k]) => k !== "items")), update, "cases")}
                {de.cases.items.map((deItem, ci) => (
                  <div key={deItem.id} className="mt-6 border-t-2 border-ink/20 pt-6">
                    <p className="font-serif text-xl tracking-tight mb-2">Case {deItem.num}: {deItem.name}</p>
                    {renderNode(["cases", "items", ci], deItem, en.cases.items[ci], update, "Feld")}
                    {caseImageManager(deItem.id)}
                  </div>
                ))}
              </div>
            </details>
          );
        }
        return (
          <details key={key} className={sectionCls} data-testid={`content-section-${key}`}>
            <summary className="font-serif text-2xl tracking-tight cursor-pointer list-none flex items-center justify-between">
              {SECTION_LABELS[key]}
              <span className="font-mono text-xs text-smoke" aria-hidden="true">+</span>
            </summary>
            <div className="mt-4">{renderNode([key], de[key], en[key], update, key)}</div>
          </details>
        );
      })}

      {/* SAVE */}
      <div className="sticky bottom-0 bg-paper border-t border-hairline py-4 flex flex-wrap items-center gap-4">
        <button data-testid="content-save-button" onClick={save} disabled={saving} className="border border-ink bg-ink text-paper px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-transparent hover:text-ink disabled:opacity-50">
          {saving ? "Speichere …" : "Änderungen speichern"}
        </button>
        {status && <p data-testid="content-status" className="font-mono text-[11px] text-smoke">{status}</p>}
      </div>
    </div>
  );
};
